import React, { useEffect, useRef, useState } from "react";
import { 
	RCCBadge, 
	RCCButton, 
	RCCGrid, 
	RCCTooltip,  
	RCCInput,
	RCCCustomServicesApi,
	RCCIcon,
	RCCHookToast,
	RCCSelectOptionsCard
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import Pagination from "src/components/Pagination";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import { useAuth } from "src/context/Auth/context";

import formatDate from "src/utils/formatDate";
import { standardErrorMessages } from "src/utils/errors";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { IRefComponentSelectOptionsCard } from "react-common-components/dist/components/SelectOptionsCard/interface";
import { IRefSuggestionsModals, ISuggestions, SuggestionSituation } from "./interface";
import ColTitle from "./styles";
import Modals from "./modals";

export const badgeSuggestions = (situation: SuggestionSituation, translate: Function) => {
	let color: "blue" | "green" | "red" | "grey" = "grey";
	let text = "";
	switch(situation){
	case SuggestionSituation.ANALYSIS:{
		color = "blue";
		text = translate("Em análise");
		break;
	}
	case SuggestionSituation.APPROVED:{
		color = "green";
		text = translate("Aprovada");
		break;
	}
	case SuggestionSituation.CANCELED:{
		color = "red";
		text = translate("Cancelada");
		break;
	}
	case SuggestionSituation.FAILED:{
		color = "red";
		text = translate("Reprovada");
		break;
	}
	case SuggestionSituation.FINISHED:{
		color = "green";
		text = translate("Concluída");
		break;
	}
	default:{
		color = "grey";
		text = translate("Aguardando análise");
	}
	}
	return (
		<RCCBadge color={color} styleInline="font-size: 0.83rem;">
			{text}
		</RCCBadge>
	);
};

export const tagsOfSuggestions = (tags: {
	Efa018Descricao: string;
}[], translate: Function) => {
	const arrayOfTags = tags && tags.length > 0 ? tags.map((sgtba004: any) => translate(sgtba004.Efa018Descricao)) : [];
	return (
		<>
			{arrayOfTags.length > 0 && (
				<div style={{ display: "flex", alignItems: "center", color: "var(--grey)", fontSize: "0.7rem" }}>
					<RCCIcon 
						name="tags"
						color="var(--grey)"
					/>
					{arrayOfTags.join(", ")}
				</div>
			)}
		</>
	);
};

const Suggestions: React.FC = () => { 
	const { translate, currentLanguage } = useTranslation();
	const { businessGroup, user } = useAuth();
	const {addToast} = RCCHookToast();
	const refModals = useRef<IRefSuggestionsModals>(null);
	const { REACT_APP_CONNECTOR_HOST } = process.env;
	const CustomServicesApi = new RCCCustomServicesApi(REACT_APP_CONNECTOR_HOST ?? "");
	const [listData, setListData] = useState<ISuggestions[]>([] as ISuggestions[]);
	const [optionsMarkers, setOptionsMarkers] = useState<{
		value: string;
		label: string;
	}[]>([]);

	const loadData = async () => {
		try{
			const response = await CustomServicesApi.getRequest({
				method: "Sugestoes/ListarSugestoes",
				urlParameters: {
					sistema: 14,
					empresa: businessGroup.efa001Id,
					usuario: user.efa005Id,
					sistemas: 14,
					sugestoes: filters.mine === "M" ? 1 : 0,
					situacoes: filters.situations,
					marcadores: filters.bookmarks,
					filtroLivre: filters.filter,
					dtInicial: filters.startDate !== null ? formatDate(filters.startDate) : "",
					dtFinal: filters.endDate !== null ? formatDate(filters.endDate) : "",
					p: 1,
					q: 1000000 
				}
			});
			setListData(response.data.list ?? [] as ISuggestions[]);
			paginationRef.current?.reload();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar sugestões"), addToast);
		}
	};

	const loadMarkers = async () => {	
		try{
			const response = await CustomServicesApi.getRequest({
				method: "Sugestoes/Eflva018",
				urlParameters: {
					sistemas: 14
				}
			});
			setOptionsMarkers(response.data.map((dt: { value: string, text: string}) => ({
				value: dt.value,
				label: translate(dt.text)
			})));
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar marcadores"), addToast);
		}
	};
	const loadConnectorConfigUser =  async () => {
		const formData = new FormData();
		formData.append("sistema", "14");
		formData.append("empresa", businessGroup.efa001Id.toString());
		formData.append("fantasia", businessGroup.efa001Fantasia);
		formData.append("usuario", user.efa005Id.toString());
		formData.append("nome", user.efa005Nome);

		await CustomServicesApi.postRequest({
			method: "Sugestoes/ConfigurarUsuario",
			contentType: "multipart/form-data",
			body: formData
		});
		
		loadData();
	};

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const refSuggestions = useRef<IRefComponentSelectOptionsCard>(null);
	const [filters, setFilters] = useState<{ 
		filter: string,
		mine: string,
		situations: string,
		bookmarks: string,
		startDate: Date | null,
		endDate: Date | null
	}>({ 
		filter: "", 
		mine: "T",
		situations: "",
		bookmarks: "",
		startDate: null,
		endDate: null
	});
	const { handleSubmit : handleSubmitFilters, control:controlFilters, register : registerFilters, reset: resetFilters, setValue: setValueFilters } = useForm();
	const submitFilters = (data: any) => {
		setFilters({
			bookmarks: data.bookmarks && data.bookmarks.length > 0 && data.bookmarks.length > 0 ? data.bookmarks.map((book: any) => book.value).join(",") : "",
			endDate: data.endDate ?? null,
			startDate: data.startDate ?? null,
			mine: data.mine,
			filter: data.filter,
			situations: data.situations && data.situations.length > 0 && data.situations.length > 0 ? data.situations.map((situation: any) => situation.value).join(",") : ""
		});
	};


	useEffectSkipInitialRender(() => {
		loadData();
	}, [filters]);

	useEffect(() => {
		loadMarkers();
		loadConnectorConfigUser();
	}, []);

	// List & Pagination
	const paginationRef = useRef<IForwardPagination>(null);

	const colSituacao = (row: any) => badgeSuggestions(row.Sga001Situacao, translate);

	const colTitle = (row: any) => (
		<ColTitle>
			<span onClick={() => { refModals.current?.actionButtonView(row); }} aria-hidden="true">{row.Sga001Titulo}</span>
			{tagsOfSuggestions(row.Sgtba004, translate)}
		</ColTitle>
	);

	const colDate = (row: any) => <>{row.Sga001Data !== null ? formatDate(new Date(Number(row.Sga001Data.replace(/\D/g,""))), currentLanguage, true) : ""}</>;
	const colDateComment = (row: any) => <>{row.DataUltimoComentario !== null ? formatDate(new Date(Number(row.DataUltimoComentario.replace(/\D/g,""))), currentLanguage, true) : ""}</>;

	const colDetails = (row: any) => (
		<RCCGrid container item>
			<RCCBadge 
				color="green"
				styleInline="font-size: 0.83rem; min-width: 46px; justify-content: space-between;"
			>
				<RCCIcon 
					name="thumbs"
				/>
				{row.QtdeAvaliacoesPositivas}
			</RCCBadge>
			<RCCBadge 
				color="red"
				styleInline="font-size: 0.83rem; min-width: 46px; justify-content: space-between;"
			>
				<RCCIcon 
					name="thumbsDown"
				/>
				{row.QtdeAvaliacoesNegativas * -1}
			</RCCBadge>
			<RCCBadge 
				color="blue"
				styleInline="font-size: 0.83rem; min-width: 46px; justify-content: space-between;"
			>
				<RCCIcon 
					name="commentFilled"
				/>
				{row.QtdeComentarios}
			</RCCBadge>
		</RCCGrid>
	);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Sugestões")}
					icon={{
						name: "thumbs"
					}}
				>
					<RCCTooltip
						text={translate("Nova sugestão")}
						color="purple"
					>
						<RCCButton
							icon={{name: "plus", color: "#FFF", size: 18}}
							color="purple"
							layout="isRounded"
							onClick={() => { refModals.current?.actionButtonAdd(); }}
							size="md"
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || 
							filters.filter || 
							filters.bookmarks ||
							filters.startDate ||
							filters.endDate || 
							filters.situations ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || 
								filters.filter || 
								filters.bookmarks ||
								filters.startDate ||
								filters.endDate || 
								filters.situations ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>

				{/* Filters */}
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => {
						resetFilters({ 
							filter: "", 
							mine: filters.mine,
							situations: "",
							bookmarks: "",
							startDate: null,
							endDate: null
						});
					}}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
							>
								<DateTimePickerRange
									control={controlFilters}
									label={translate("Período")}
									labelSeparator={translate("até")}
									nameInit = "startDate"
									nameEnd = "endDate"
									type="datePicker"
									setValue={setValueFilters}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
							>
								<Select 
									control={controlFilters}
									name="situations" 
									options={[
										{value: SuggestionSituation.ANALYSIS.toString(), label: translate("Em análise")}, 
										{value: SuggestionSituation.APPROVED.toString(), label: translate("Aprovada")}, 
										{value: SuggestionSituation.CANCELED.toString(), label: translate("Cancelada")}, 
										{value: SuggestionSituation.FAILED.toString(), label: translate("Reprovada")}, 
										{value: SuggestionSituation.FINISHED.toString(), label: translate("Concluída")}, 
										{value: SuggestionSituation.WAITING_ANALYSIS.toString(), label: translate("Aguardando análise")}
									]} 
									label={translate("Situação")}
									isMulti
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
							>
								<Select 
									control={controlFilters}
									name="bookmarks" 
									options={optionsMarkers} 
									isMulti
									label={translate("Marcadores")}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
							>
								<RCCInput
									name="filter" label={translate("Pesquisar")}
									register={registerFilters}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>

				<RCCGrid container item justifyContent="flex-end">	
					<RCCSelectOptionsCard 
						ref={refSuggestions}
						control={controlFilters}
						name="mine" 
						options={[
							{value: "T", label: translate("Todas sugestões")},
							{value: "M", label: translate("Minhas sugestões")} 
						]} 
						onChange={(value: string) => {
							setFilters((prevState) => ({
								...prevState,
								mine: value
							}));
						}}
						defaultValue="T"
					/>					
				</RCCGrid>

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute={listData}    
					columns={[
	  					{
							idColumn: "title",
							nameColumn: translate("Título"),
							xs: 12,
							sm: 4,
							md: 4,
							cellRenderer: colTitle
	  					},
						  {
							idColumn: "date",
							nameColumn: translate("Data"),
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: colDate
	  					},
						  {
							idColumn: "title",
							nameColumn: translate("Data do último comentário"),
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: colDateComment
	  					},
						{
							idColumn: "situation",
							nameColumn: translate("Situação"),
							xs: 6,
							sm: 2,
							md: 2,
							cellRenderer: colSituacao
	  					},
						{
							idColumn: "details",
							nameColumn: "",
							xs: 6,
							sm: 2,
							md: 2,
							cellRenderer: colDetails
						}
					]}
				/>
			</RCCGrid>

			<Modals 
				ref={refModals} 
				reload={loadData} 
				optionsMarkers={optionsMarkers}
				CustomServicesApi={CustomServicesApi}
			/>

		</>
	);
};

export default Suggestions;
